/**
 * The schedules, their activities, the calendar and the date negotiation.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * THE PRIVACY NOTE THIS MODULE CARRIES FOR EVERY SCREEN THAT USES IT
 *   The assessor fields on an activity come back EMPTY for a counterparty principal,
 *   by SQL inside the aggregate, and the team endpoint answers an empty list for the
 *   same principal. The screens render what arrives: an empty list is an empty list,
 *   and nothing here re-asks or re-decides.
 */

import { apiJson, bool, listBody, num, optStr, query, str, strArray } from "./client";

export interface ScheduleSummary {
  id: string;
  humanId: string;
  title: string;
  year: number;
  revision: number;
  workflowState: string;
  projectId: string;
  projectHumanId: string;
  projectName: string;
  stageCode: string | null;
  stageLabel: string | null;
  distributedAt: string | null;
  /**
   * Wave W2: the lifecycle events THIS caller may take now (state machine and predicate
   * together). Filled on the single-schedule read; the page draws only these.
   */
  availableEvents: string[];
  /** Whether the caller may plan an activity into this plan now (a draft it may write). */
  mayPlanActivity: boolean;
}

/** Who holds the next move on a planned date (wave W2). */
export type NegotiationState =
  "NOT_DISTRIBUTED" | "AWAITING_CONTRACTOR" | "AWAITING_OPERATOR" | "AGREED" | "DONE";

export interface Activity {
  id: string;
  humanId: string;
  scheduleId: string;
  scheduleWorkflowState: string;
  plannedDate: string;
  plannedEndDate: string | null;
  status: string;
  isTerminal: boolean;
  priority: string | null;
  purpose: string | null;
  checklistReference: string | null;
  contractorPartyId: string;
  contractorPartyName: string;
  contractId: string | null;
  verificationTypeCode: string;
  verificationTypeLabel: string;
  assessorPersonIds: string[];
  assessorDisplayNames: string[];
  /** Wave W2: whose turn it is on this date, and the date waiting for an answer. */
  negotiationState: NegotiationState;
  pendingDate: string | null;
  /** The date moves THIS caller may make now. */
  availableMoves: NegotiationMove[];
  /** Wave Experience A: an open act of mine is about it (calendar feeds carry it). */
  actionableByMe?: boolean;
  /** The status acts (CONDUCT, CLOSE, CANCEL) the caller may take now; single read only. */
  availableEvents: string[];
  /** The inspection day (single read only): the record its conduct opened, if any. */
  inspectionId: string | null;
  /** Whether THIS caller may conduct it now with the program's checklist. */
  mayConductInspection: boolean;
}

export interface Assessor {
  personId: string;
  personDisplayName: string;
  disciplineLabel: string | null;
  isLead: boolean;
  roleLabel: string;
  assignmentStatus: string;
}

export interface NegotiationEntry {
  id: string;
  move: string;
  proposedDate: string | null;
  justification: string | null;
  at: string;
  actorDisplayName: string;
  actingPartyName: string;
}

export interface TransitionEntry {
  id: string;
  event: string;
  fromState: string | null;
  toState: string | null;
  comment: string | null;
  at: string;
  actorDisplayName: string | null;
}

export interface CalendarConflict {
  kind: string;
  kindLabel: string;
  enforcement: string;
  periodName: string;
  startsOn: string;
  endsOn: string;
  activityHumanId: string | null;
}

export interface CalendarPeriod {
  id: string;
  kind: string;
  kindLabel: string;
  enforcement: string;
  name: string;
  startsOn: string;
  endsOn: string;
  note: string | null;
}

function assertRecord(body: unknown): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new Error("Expected an object body");
  }
  return body as Record<string, unknown>;
}

function schedule(body: Record<string, unknown>): ScheduleSummary {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    title: str(body.title),
    year: num(body.year),
    revision: num(body.revision),
    workflowState: str(body.workflow_state),
    projectId: str(body.project_id),
    projectHumanId: str(body.project_human_id),
    projectName: str(body.project_name),
    stageCode: optStr(body.stage_code),
    stageLabel: optStr(body.stage_label),
    distributedAt: optStr(body.distributed_at),
    availableEvents: strArray(body.available_events ?? []),
    mayPlanActivity: body.may_plan_activity === true,
  };
}

const NEGOTIATION_STATES: readonly NegotiationState[] = [
  "NOT_DISTRIBUTED",
  "AWAITING_CONTRACTOR",
  "AWAITING_OPERATOR",
  "AGREED",
  "DONE",
];

export function negotiationState(raw: unknown): NegotiationState {
  return NEGOTIATION_STATES.find((one) => one === raw) ?? "NOT_DISTRIBUTED";
}

const MOVES: readonly NegotiationMove[] = ["ACKNOWLEDGE", "PROPOSE", "CONFIRM", "REJECT"];

function moves(raw: unknown): NegotiationMove[] {
  return strArray(raw ?? []).flatMap((one) => MOVES.filter((move) => move === one));
}

export async function listSchedules(
  token: string,
  filters: {
    projectId?: string | null;
    year?: number | null;
    /** Wave W1: the signatures queue, only plans waiting on the caller's endorsement or approval. */
    pendingForMe?: boolean;
    signal?: AbortSignal;
  } = {},
): Promise<{ total: number; items: ScheduleSummary[] }> {
  const body = await apiJson(
    token,
    `/schedules${query({
      project_id: filters.projectId,
      year: filters.year,
      pending_for_me: filters.pendingForMe === true ? true : undefined,
    })}`,
    listBody,
    { signal: filters.signal },
  );
  return { total: body.total, items: body.items.map(schedule) };
}

export async function getSchedule(
  token: string,
  scheduleId: string,
  signal?: AbortSignal,
): Promise<ScheduleSummary> {
  return apiJson(token, `/schedules/${scheduleId}`, (raw) => schedule(assertRecord(raw)), {
    signal,
  });
}

export interface ScheduleCreateInput {
  readonly projectId: string;
  readonly year: number;
  readonly title?: string;
  readonly stageId?: string | null;
}

export async function createSchedule(
  token: string,
  input: ScheduleCreateInput,
): Promise<ScheduleSummary> {
  return apiJson(token, "/schedules", (raw) => schedule(assertRecord(raw)), {
    method: "POST",
    body: {
      project_id: input.projectId,
      year: input.year,
      title:
        input.title && input.title.trim().length > 0
          ? input.title.trim()
          : `${String(input.year)} inspection plan`,
      stage_id: input.stageId ?? null,
    },
  });
}

function activity(body: Record<string, unknown>): Activity {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    scheduleId: str(body.schedule_id),
    scheduleWorkflowState: str(body.schedule_workflow_state),
    plannedDate: str(body.planned_date),
    plannedEndDate: optStr(body.planned_end_date),
    status: str(body.status),
    isTerminal: bool(body.is_terminal),
    priority: optStr(body.priority),
    purpose: optStr(body.purpose),
    checklistReference: optStr(body.checklist_reference),
    contractorPartyId: optStr(body.contractor_party_id) ?? "",
    contractorPartyName: str(body.contractor_party_name),
    contractId: optStr(body.contract_id),
    verificationTypeCode: str(body.verification_type_code),
    verificationTypeLabel: str(body.verification_type_label),
    assessorPersonIds: strArray(body.assessor_person_ids ?? []),
    assessorDisplayNames: strArray(body.assessor_display_names ?? []),
    negotiationState: negotiationState(body.negotiation_state),
    pendingDate: optStr(body.pending_date),
    availableMoves: moves(body.available_moves),
    availableEvents: strArray(body.available_events ?? []),
    inspectionId: optStr(body.inspection_id),
    mayConductInspection: body.may_conduct_inspection === true,
  };
}

export async function listActivities(
  token: string,
  scheduleId: string,
  signal?: AbortSignal,
): Promise<{ total: number; items: Activity[] }> {
  const body = await apiJson(token, `/schedules/${scheduleId}/activities`, listBody, { signal });
  return { total: body.total, items: body.items.map(activity) };
}

export async function getActivity(
  token: string,
  activityId: string,
  signal?: AbortSignal,
): Promise<Activity> {
  return apiJson(token, `/activities/${activityId}`, (raw) => activity(assertRecord(raw)), {
    signal,
  });
}

function assessor(body: Record<string, unknown>): Assessor {
  return {
    personId: str(body.person_id),
    personDisplayName: str(body.person_display_name),
    disciplineLabel: optStr(body.discipline_label),
    isLead: bool(body.is_lead),
    roleLabel: str(body.role_label),
    assignmentStatus: str(body.assignment_status),
  };
}

export async function listAssessors(
  token: string,
  activityId: string,
  signal?: AbortSignal,
): Promise<Assessor[]> {
  return apiJson(
    token,
    `/activities/${activityId}/assessors`,
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of assessors");
      }
      return raw.map((entry) => assessor(assertRecord(entry)));
    },
    { signal },
  );
}

function negotiation(body: Record<string, unknown>): NegotiationEntry {
  return {
    id: str(body.id),
    move: str(body.move),
    proposedDate: optStr(body.proposed_date),
    justification: optStr(body.justification),
    at: str(body.at),
    actorDisplayName: str(body.actor_display_name),
    actingPartyName: str(body.acting_party_name),
  };
}

export async function listNegotiation(
  token: string,
  activityId: string,
  signal?: AbortSignal,
): Promise<NegotiationEntry[]> {
  return apiJson(
    token,
    `/activities/${activityId}/negotiation`,
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of negotiation entries");
      }
      return raw.map((entry) => negotiation(assertRecord(entry)));
    },
    { signal },
  );
}

export type NegotiationMove = "ACKNOWLEDGE" | "PROPOSE" | "CONFIRM" | "REJECT";

export interface NegotiationMovePayload {
  move: NegotiationMove;
  proposedDate?: string | null;
  justification?: string | null;
  inReplyToId?: string | null;
}

/**
 * One move in the exchange. A CONFIRM is the only move that changes the plan of record.
 *
 * Wave W1.5 forms sweep: the body is the server's snake_case. It used to be sent as the
 * client's camelCase object, so `proposedDate` never reached the server (it ignores
 * unknown members) and every PROPOSE or CONFIRM from the screen was refused with "This
 * move names the date it is about" although a date had been picked.
 */
export async function recordNegotiationMove(
  token: string,
  activityId: string,
  payload: NegotiationMovePayload,
): Promise<{ move: string; plannedDate: string }> {
  return apiJson(
    token,
    `/activities/${activityId}/negotiation`,
    (raw) => {
      const body = assertRecord(raw);
      return { move: str(body.move), plannedDate: str(body.planned_date) };
    },
    {
      method: "POST",
      body: {
        move: payload.move,
        proposed_date: payload.proposedDate ?? null,
        justification: payload.justification ?? null,
        in_reply_to_id: payload.inReplyToId ?? null,
      },
    },
  );
}

function transition(body: Record<string, unknown>): TransitionEntry {
  return {
    id: str(body.id),
    event: str(body.event),
    fromState: optStr(body.from_state),
    toState: optStr(body.to_state),
    comment: optStr(body.comment),
    at: str(body.at),
    actorDisplayName: optStr(body.actor_display_name),
  };
}

export async function listScheduleTransitions(
  token: string,
  scheduleId: string,
  signal?: AbortSignal,
): Promise<TransitionEntry[]> {
  return apiJson(
    token,
    `/schedules/${scheduleId}/transitions`,
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of transitions");
      }
      return raw.map((entry) => transition(assertRecord(entry)));
    },
    { signal },
  );
}

/** A guarded state change on the plan, with the advisories the approval re-evaluation raised. */
export async function transitionSchedule(
  token: string,
  scheduleId: string,
  event: string,
  payload: { comment?: string | null } = {},
): Promise<{ toState: string; warnings: CalendarConflict[] }> {
  return apiJson(
    token,
    `/schedules/${scheduleId}/transitions/${event}`,
    (raw) => {
      const body = assertRecord(raw);
      const warnings = Array.isArray(body.warnings) ? body.warnings : [];
      return {
        toState: str(body.to_state),
        warnings: warnings.map((entry) => conflict(assertRecord(entry))),
      };
    },
    { method: "POST", body: payload },
  );
}

export async function transitionActivity(
  token: string,
  activityId: string,
  event: string,
  payload: { comment?: string | null; planned_date?: string | null } = {},
): Promise<{ toState: string | null }> {
  return apiJson(
    token,
    `/activities/${activityId}/transitions/${event}`,
    (raw) => {
      const body = assertRecord(raw);
      return { toState: optStr(body.to_state) };
    },
    { method: "POST", body: payload },
  );
}

function conflict(body: Record<string, unknown>): CalendarConflict {
  return {
    kind: str(body.kind),
    kindLabel: str(body.kind_label),
    enforcement: str(body.enforcement),
    periodName: str(body.period_name),
    startsOn: str(body.starts_on),
    endsOn: str(body.ends_on),
    activityHumanId: optStr(body.activity_human_id),
  };
}

/**
 * The preview: what this date would collide with, evaluated and never refused.
 *
 * Wave W2: the server reads `project_id` and `planned_date`. This used to send `date`
 * alone, every preview was refused, and the refusal was swallowed: Mahmoud pressed
 * "Check the date" and nothing happened.
 */
export async function previewConflicts(
  token: string,
  ask: { projectId: string; plannedDate: string; plannedEndDate?: string | null },
  signal?: AbortSignal,
): Promise<CalendarConflict[]> {
  return apiJson(
    token,
    `/calendar-periods/conflicts${query({
      project_id: ask.projectId,
      planned_date: ask.plannedDate,
      planned_end_date: ask.plannedEndDate ?? undefined,
    })}`,
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of conflicts");
      }
      return raw.map((entry) => conflict(assertRecord(entry)));
    },
    { signal },
  );
}

function period(body: Record<string, unknown>): CalendarPeriod {
  return {
    id: str(body.id),
    kind: str(body.kind),
    kindLabel: str(body.kind_label),
    enforcement: str(body.enforcement),
    name: str(body.name),
    startsOn: str(body.starts_on),
    endsOn: str(body.ends_on),
    note: optStr(body.note),
  };
}

export async function listCalendarPeriods(
  token: string,
  signal?: AbortSignal,
): Promise<CalendarPeriod[]> {
  return apiJson(
    token,
    "/calendar-periods",
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of periods");
      }
      return raw.map((entry) => period(assertRecord(entry)));
    },
    { signal },
  );
}

export interface ActivityCreatePayload {
  contractor_party_id: string;
  verification_type_id: string;
  planned_date: string;
  planned_end_date?: string | null;
  contract_id?: string | null;
  planning_candidate_id?: string | null;
  purpose?: string | null;
  checklist_reference?: string | null;
  priority?: string | null;
}

/** Plan one activity. The calendar is evaluated before it is written; warnings come back. */
export async function createActivity(
  token: string,
  scheduleId: string,
  payload: ActivityCreatePayload,
): Promise<{ id: string; humanId: string; warnings: CalendarConflict[] }> {
  return apiJson(
    token,
    `/schedules/${scheduleId}/activities`,
    (raw) => {
      const body = assertRecord(raw);
      const warnings = Array.isArray(body.warnings) ? body.warnings : [];
      return {
        id: str(body.id),
        humanId: str(body.human_id),
        warnings: warnings.map((entry) => conflict(assertRecord(entry))),
      };
    },
    { method: "POST", body: payload },
  );
}

/** A project the caller may plan in from the calendar, and whether it may also submit. */
export interface PlanningProject {
  id: string;
  humanId: string;
  name: string;
  maySubmit: boolean;
}

export async function listPlanningProjects(
  token: string,
  signal?: AbortSignal,
): Promise<PlanningProject[]> {
  return apiJson(
    token,
    "/schedules/planning-projects",
    (raw) => {
      if (!Array.isArray(raw)) {
        throw new Error("Expected an array of projects");
      }
      return raw.map((entry) => {
        const body = assertRecord(entry);
        return {
          id: str(body.id),
          humanId: str(body.human_id),
          name: str(body.name),
          maySubmit: bool(body.may_submit),
        };
      });
    },
    { signal },
  );
}

/** Plan an activity from the calendar (wave W2): the wire body, field by field. */
export interface CalendarPlanPayload {
  project_id: string;
  contractor_party_id: string;
  verification_type_id: string;
  planned_date: string;
  planned_end_date?: string | null;
  contract_id?: string | null;
  planning_candidate_id?: string | null;
  purpose?: string | null;
  submit: boolean;
}

export interface CalendarPlanned {
  scheduleId: string;
  scheduleHumanId: string;
  scheduleCreated: boolean;
  activityId: string;
  activityHumanId: string;
  submitted: boolean;
  warnings: CalendarConflict[];
}

export async function planFromCalendar(
  token: string,
  payload: CalendarPlanPayload,
): Promise<CalendarPlanned> {
  return apiJson(
    token,
    "/schedules/plan-activity",
    (raw) => {
      const body = assertRecord(raw);
      const warnings = Array.isArray(body.warnings) ? body.warnings : [];
      return {
        scheduleId: str(body.schedule_id),
        scheduleHumanId: str(body.schedule_human_id),
        scheduleCreated: bool(body.schedule_created),
        activityId: str(body.activity_id),
        activityHumanId: str(body.activity_human_id),
        submitted: bool(body.submitted),
        warnings: warnings.map((entry) => conflict(assertRecord(entry))),
      };
    },
    { method: "POST", body: payload },
  );
}
